import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import StampDeleteModal from "./StampDeleteModal";
import StampUpdateModal from "./StampUpdateModal";
import Stamp from "../../models/stamp";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useIsMobile } from "../../hooks/is-mobile";
import { StampCard } from "./StampMobileCard";
import StampCrudActions from "./StampCrudActions";

const ITEMS_PER_PAGE = 10;

const StampList = () => {
  const fields = [
    { key: "identifier", _classes: ["text-center"], label: "Código" },
    { key: "beginDate", _classes: ["text-center"], label: "Descripción" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showStampEditModal, setShowStampEditModal] = useState(false);
  const [showStampDeleteModal, setShowStampDeleteModal] = useState(false);
  const [stamp, setStamp] = useState<Stamp | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [stamps, setStamps] = useState<Stamp[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    return { page: page };
  };

  const fetchStamps = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());
    const itemsRequestStatus = await getList<Stamp>(
      "/stamps/",
      limit,
      offset,
      additionalParams,
    );

    if (itemsRequestStatus.status === SUCCESS) {
      if (itemsRequestStatus.data !== undefined) {
        const count = itemsRequestStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setStamps(itemsRequestStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = itemsRequestStatus.detail
        ? itemsRequestStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  /*const reset = () => {
    history.push(history.location.pathname);
  };*/

  const onAddClick = () => {
    setStamp(undefined);
    setShowStampEditModal(true);
  };

  const onUpdateClick = (stamp: Stamp) => {
    setStamp(stamp);
    setShowStampEditModal(true);
  };

  const onDeleteClick = (stamp: Stamp) => {
    setStamp(stamp);
    setShowStampDeleteModal(true);
  };

  const onStampEditCancel = () => {
    setShowStampEditModal(false);
  };

  const onStampEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowStampEditModal(false);
    setStamps([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchStamps();
  };

  const onStampDeleteCancel = () => {
    setShowStampDeleteModal(false);
  };

  const onStampDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowStampDeleteModal(false);
    setStamps([]);
    setLoading(true);
    fetchStamps();
  };

  useEffect(() => {
    fetchStamps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("stamps")) {
        return;
      }
      fetchStamps();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    setLoading(true);
    fetchStamps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);
  const isMobile = useIsMobile();
  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Timbrados</h3>
              </div>
              {!taxPayer.isDemo && (
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </div>
              )}
            </CCardHeader>
            <CCardBody>
              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {stamps.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      stamps.map((entry) => (
                        <StampCard
                          key={entry.id}
                          stamp={entry}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                          isDemo={!!taxPayer.isDemo}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={stamps}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      beginDate: (item: Stamp) => {
                        const srtDate = item.beginDate
                          ? item.beginDate.slice(0, -1)
                          : "-";
                        const renderDate =
                          srtDate === "-"
                            ? srtDate
                            : new Date(srtDate).toLocaleDateString();
                        return <td className="text-center">{renderDate}</td>;
                      },
                      actions: (item: Stamp) => (
                        <StampCrudActions
                          stamp={item}
                          onDeleteClick={onDeleteClick}
                          onUpdateClick={onUpdateClick}
                          isDemo={!!taxPayer.isDemo}
                        ></StampCrudActions>
                      ),
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <StampUpdateModal
        show={showStampEditModal}
        onCancel={onStampEditCancel}
        onSuccess={onStampEditSuccess}
        stamp={stamp}
      />
      <StampDeleteModal
        show={showStampDeleteModal}
        onCancel={onStampDeleteCancel}
        onSuccess={onStampDeleteSuccess}
        stamp={stamp}
      />
    </>
  );
};

export default StampList;
